import React, { useEffect, useId, useRef, useState } from 'react';
import { gameApi, GameState, Monster, MonsterAssumedCast, MonsterCheckPreview, MonsterComparisonError, MonsterComparisonResult } from '../api';
import { MonsterBreakdown } from './MonsterBreakdown';
import { checkProbability, formatCheckProbability } from './checkProbability';

type Draft = { owner: string; basis: MonsterComparisonResult; equippedRefs: string[]; casts: MonsterAssumedCast[] };
const tabs = ['Evade', 'Horror', 'Combat'] as const;
type Tab = typeof tabs[number];

function rebase(draft: Draft, basis: MonsterComparisonResult): { equippedRefs: string[]; casts: MonsterAssumedCast[] } | undefined {
  const translate = (reference: string) => {
    const original = draft.basis.items.find(item => item.reference === reference);
    if (!original) return undefined;
    // If identical copies disappeared, occurrence indices cannot prove which copy survived.
    const family = original.identity.slice(0, original.identity.lastIndexOf(':') + 1);
    const oldCount = draft.basis.items.filter(item => item.identity.startsWith(family)).length;
    const newCount = basis.items.filter(item => item.identity.startsWith(family)).length;
    if (oldCount !== newCount && Math.max(oldCount, newCount) > 1) return undefined;
    return basis.items.find(item => item.identity === original.identity)?.reference;
  };
  const equipped = draft.equippedRefs.map(translate);
  const casts = draft.casts.map(cast => ({ ...cast, itemRef: translate(cast.itemRef) }));
  if (equipped.some(value => !value) || casts.some(cast => !cast.itemRef)) return undefined;
  return { equippedRefs: equipped as string[], casts: casts as MonsterAssumedCast[] };
}

function CheckDetails({ tab, check }: { tab: Tab; check?: MonsterCheckPreview | null }) {
  if (check?.status !== 'available' || !check.breakdown) return <p>{check?.note ?? 'Preview unavailable'}</p>;
  const chance = check.successThreshold != null && check.requiredSuccesses != null
    ? checkProbability(check.breakdown.total, check.requiredSuccesses, check.successThreshold) : undefined;
  return <><div className="monster-check-dice"><MonsterBreakdown title={`${tab} dice`} breakdown={check.breakdown} />
    <strong>{check.breakdown.total} dice</strong></div>
    <dl className="monster-check-details"><div><dt>Success threshold</dt><dd>{check.successThreshold != null ? `${check.successThreshold}+` : 'Unavailable'}</dd></div>
      <div><dt>Required successes</dt><dd>{check.requiredSuccesses ?? 'Unavailable'}</dd></div>
      <div><dt>Approx. success rate</dt><dd className={chance === 0 ? 'monster-check-impossible' : undefined}>{chance == null ? 'Unavailable' : formatCheckProbability(chance)}</dd></div></dl>
    <p className="monster-check-estimate-note">Initial dice roll only. Rerolls, Clue spending, casting chances, and special rule outcomes are excluded.</p></>;
}

export function MonsterComparison({ state, monster, investigatorName, viewerName }: {
  state?: GameState; monster: Monster; investigatorName: string; viewerName?: string;
}) {
  const [tab, setTab] = useState<Tab>('Evade');
  const id = useId();
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const gameId = state?.gameId;
  const owner = `${gameId}:${monster.id || monster.name}:${investigatorName}`;
  const revision = state?.comparisonRevision;
  const [draft, setDraft] = useState<Draft>();
  const [loaded, setLoaded] = useState<{ owner: string; result: MonsterComparisonResult }>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [switching, setSwitching] = useState<{ owner: string; target: string; remove: string[] }>();
  const [activeOwner, setActiveOwner] = useState(owner);
  if (activeOwner !== owner) {
    setActiveOwner(owner); setDraft(undefined); setLoaded(undefined); setSwitching(undefined); setError(''); setNotice('');
  }
  const result = loaded?.owner === owner ? loaded.result : undefined;
  const relevantDraft = draft?.owner === owner ? draft : undefined;
  const supported = !!gameId && !!monster.comparisonRef && revision != null;
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    if (!supported || !gameId || revision == null || !monster.comparisonRef) { setLoading(false); return; }
    const controller = new AbortController();
    let current = true;
    setLoading(true); setError('');
    const get = (equipment?: { equippedRefs: string[]; casts: MonsterAssumedCast[] }) => gameApi.getMonsterComparison(viewerName,
      gameId, { revision, monsterRef: monster.comparisonRef!, investigatorName,
        ...(equipment ? { equippedRefs: equipment.equippedRefs, casts: equipment.casts } : {}) }, controller.signal);
    (async () => {
      let value: MonsterComparisonResult;
      if (relevantDraft && relevantDraft.basis.revision !== revision) {
        const baseline = await get();
        const translated = rebase(relevantDraft, baseline);
        if (!translated) {
          value = baseline;
          if (current) { setDraft(undefined); setNotice('Equipment changed. The tool reset to real equipment.'); }
        } else {
          try { value = await get(translated); }
          catch (failure) {
            if (!(failure instanceof MonsterComparisonError) || failure.status !== 400) throw failure;
            value = baseline;
            if (current) { setDraft(undefined); setNotice('The assumed loadout is no longer legal. The tool reset to real equipment.'); }
          }
        }
      } else value = await get(relevantDraft);
      if (current) setLoaded({ owner, result: value });
    })().catch(failure => {
      if (current && !controller.signal.aborted) setError(failure instanceof Error ? failure.message : 'The comparison could not be calculated.');
    }).finally(() => { if (current) setLoading(false); });
    return () => { current = false; controller.abort(); };
  }, [owner, revision, relevantDraft, supported, gameId, investigatorName, monster.comparisonRef, viewerName, refresh]);

  const previewName = Object.keys(monster.checkPreviewsByInvestigatorName ?? {}).find(name => name.toLowerCase() === investigatorName.toLowerCase());
  const checks = result?.checks ?? monster.checkPreviewsByInvestigatorName?.[previewName ?? ''];
  const check = checks?.[tab.toLowerCase() as 'evade' | 'horror' | 'combat'];
  const submit = (equippedRefs: string[], casts = result?.casts ?? []) => {
    if (!result || loading) return;
    setNotice('');
    setDraft({ owner, basis: result, equippedRefs, casts });
    setSwitching(undefined);
  };
  const switchState = switching?.owner === owner ? switching : undefined;
  const selectTab = (index: number) => { setTab(tabs[index]); tabRefs.current[index]?.focus(); };
  return <div className="monster-comparison-tool">
    <div role="tablist" aria-label="Monster checks" className="monster-check-tabs">
      {tabs.map((name, index) => <button key={name} type="button" role="tab" id={`${id}-${name}`} aria-controls={`${id}-panel`}
        aria-selected={tab === name} tabIndex={tab === name ? 0 : -1} ref={node => { tabRefs.current[index] = node; }} onClick={() => setTab(name)}
        onKeyDown={event => {
          if (['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) {
            event.preventDefault();
            selectTab(event.key === 'Home' ? 0 : event.key === 'End' ? 2 : (index + (event.key === 'ArrowRight' ? 1 : 2)) % 3);
          }
        }}><svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" strokeWidth="2">
          {name === 'Evade' ? <path d="M3 12h17m-6-6 6 6-6 6" /> : name === 'Horror'
            ? <><path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12Z" /><circle cx="12" cy="12" r="3" /></>
            : <path d="m4 3 16 17m-5 1 6-6M3 4l17 16M3 9l6-6" />}</svg>{name}</button>)}
    </div>
    {error && <p role="alert">{error} {result && 'Showing the last validated comparison.'}</p>}
    {notice && <p role="status">{notice}</p>}
    <section role="tabpanel" id={`${id}-panel`} aria-labelledby={`${id}-${tab}`} aria-busy={loading}>
      {loading && <p role="status">Calculating comparison…</p>}
      <CheckDetails tab={tab} check={check} />
      {tab === 'Combat' && <section className="monster-simulated-equipment"><h4>Simulated equipment</h4>
        <p>Tool only: these controls do not change the investigator’s equipment or spend resources.</p>
        {result ? <><p><strong>Hands: {result.occupiedHands} / {result.handCapacity}</strong></p>
          <button type="button" disabled={loading} onClick={() => { setDraft(undefined); setSwitching(undefined); setNotice(''); setRefresh(value => value + 1); }}>Reset to real equipment</button>
          <ul className="monster-equipment-list">{result.items.map(item => {
            const cast = result.casts.find(value => value.itemRef === item.reference);
            return <li key={item.reference}><div><strong>{item.name}</strong> — {item.hands} {item.hands === 1 ? 'hand' : 'hands'}
              {item.equipped && <span className="monster-equipped-label"> · Equipped</span>}{item.exhausted && <span> · Exhausted{cast ? ' (assumed)' : ''}</span>}
              {item.activeSpell && <span> · Already-active spell</span>}</div>
              <div>{item.combatBonuses.length ? item.combatBonuses.map((bonus, index) => <span className="monster-equipment-bonus" key={index}>{bonus.value >= 0 ? '+' : ''}{bonus.value} {bonus.type} Combat </span>)
                : item.equipped || cast ? result.checks.combat.status === 'available' ? 'No Combat bonus against this monster' : 'Combat bonus depends on special rules' : null}</div>
              <div className="monster-equipment-controls">
                {item.equipped ? <button type="button" disabled={loading} aria-label={`Unequip ${item.name} in tool`}
                  onClick={() => submit(result.equippedRefs.filter(reference => reference !== item.reference), result.casts.filter(value => value.itemRef !== item.reference))}>Unequip</button>
                  : item.canEquip && <><button type="button" disabled={loading} aria-label={`Equip ${item.name} in tool`} onClick={() => submit([...result.equippedRefs, item.reference])}>Equip</button>
                    {result.equippedRefs.length > 0 && <button type="button" disabled={loading} aria-label={`Switch to ${item.name} in tool`}
                      onClick={() => setSwitching({ owner, target: item.reference, remove: [] })}>Switch</button>}</>}
                {cast ? <><label>Assumed casting successes for {item.name}<input key={`${item.reference}-${cast.successes}`} type="number" min="1" max="1000" defaultValue={cast.successes} disabled={loading}
                  onBlur={event => { const successes = Number(event.target.value); if (successes !== cast.successes) submit(result.equippedRefs, result.casts.map(value => value === cast ? { ...value, successes } : value)); }} /></label>
                  <button type="button" disabled={loading} aria-label={`Remove assumed cast of ${item.name}`} onClick={() => submit(result.equippedRefs, result.casts.filter(value => value !== cast))}>Remove assumed cast</button></>
                  : item.canAssumeCast && <button type="button" disabled={loading || item.activeSpell || item.exhausted} aria-label={`Assume successful cast of ${item.name}`}
                    onClick={() => submit(result.equippedRefs, [...result.casts, { itemRef: item.reference, successes: 1 }])}>Assume successful cast</button>}
              </div>
            </li>;
          })}</ul>
          {result.items.length === 0 && <p>No equipable items.</p>}
          {switchState && <fieldset><legend>Choose equipment to replace</legend>
            {result.items.filter(item => item.equipped).map(item => <label key={item.reference}><input type="checkbox" checked={switchState.remove.includes(item.reference)}
              onChange={event => setSwitching({ ...switchState, remove: event.target.checked ? [...switchState.remove, item.reference] : switchState.remove.filter(value => value !== item.reference) })} />{item.name} ({item.hands} hands)</label>)}
            <button type="button" disabled={loading} onClick={() => submit([...result.equippedRefs.filter(value => !switchState.remove.includes(value)), switchState.target])}>Apply switch</button>
            <button type="button" onClick={() => setSwitching(undefined)}>Cancel switch</button>
          </fieldset>}
        </> : <p>{supported ? 'Loading equipment…' : 'Equipment simulation is unavailable for this reference.'}</p>}
      </section>}
    </section>
  </div>;
}
